import { Col, Row } from 'antd';
import Table from 'common/table/table';
import { Icons, RowIconAction, ThemeHeader } from 'common/table/table-helper';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { isToggleEnabled } from 'react-awesome-feature-toggles/dist/feature-toggles';
import { FormattedMessage, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { Button, Div, Input, SubTitle } from 'ui';
import {
  CHANGE_INDICATOR_ADD,
  CHANGE_INDICATOR_DELETE,
  CHANGE_INDICATOR_EDIT,
} from 'utils/change-indicators';
import { v4 as uuidv4 } from 'uuid';
import PermissionCheck from '../../../common/features/permission-check';
import FixedOperations from './fixed-operations';

const StyledOperationFooter = styled(Row)`
  padding: 0 20px 20px;
`;

const Wrapper = styled(Div)`
  box-shadow: rgba(0, 0, 0, 0.27) 0px;  
  border-radius: 4px;
  background-color: #fff;
  margin-bottom: 20px;
`;

const TaskList = ({
  taskList,
  isDraft,
  disabled,
  isLoading,
  fromScratch,
  intl: { formatMessage: translate },
  form,
  setFormDirty,
  orderId,
  getTaskListRequest,
  activeStatus,
}) => {
  const [operationValue, setOperationValue] = useState(null);
  const formTaskLists = form.getFieldValue('taskList') || {};
  const { operations = [] } = formTaskLists;

  useEffect(() => {
    if (taskList?.id && taskList?.id !== formTaskLists?.id) {
      form.setFieldsValue({
        taskList,
      });
    }
  }, [taskList?.id]);

  const applyChangesToForm = (v) => {
    form.setFieldsValue({
      taskList: v,
    });
    setFormDirty();
  };

  const handleAddOperation = () => {
    if (!disabled && !isLoading) {
      const value = form.getFieldValue('taskList');
      const operation = {
        id: uuidv4(),
        code: null,
        description: operationValue,
        changeIndicator: CHANGE_INDICATOR_ADD,
      };
      const editedTaskList = {
        ...value,
        operations: value.operations ? [...value.operations, operation] : [operation],
      };

      applyChangesToForm(editedTaskList);
      setOperationValue(null);
    }
  };

  const handleDeleteOperation = (id) => {
    if (!disabled && !isLoading) {
      const value = form.getFieldValue('taskList');
      const operationToDelete = value.operations?.find(o => o.id === id);
      operationToDelete.changeIndicator = CHANGE_INDICATOR_DELETE;
      applyChangesToForm(value);
    }
  };

  const handleDrop = (dragAndDroppedOperations) => {
    if (!disabled && !isLoading) {
      const value = form.getFieldValue('taskList');
      const filteredOperations = [
        ...dragAndDroppedOperations?.filter(o => o.changeIndicator !== CHANGE_INDICATOR_DELETE),
      ];

      const editedTaskList = {
        ...value,
        operations: [
          ...value.operations?.filter(o => o.changeIndicator === CHANGE_INDICATOR_DELETE),
          ...filteredOperations?.map((o, i) => {
            if (o.id !== value.operations[i].id) {
              if (o.changeIndicator !== CHANGE_INDICATOR_ADD) {
                o.changeIndicator = CHANGE_INDICATOR_EDIT;
              }
              return o;
            }
            return o;
          }),
        ],
      };

      applyChangesToForm(editedTaskList);
    }
  };

  const handleOperationFieldChange = (name, value) => {
    if (!disabled && !isLoading) {
      const taskListValue = form.getFieldValue('taskList');

      if (taskListValue[name] && parseFloat(taskListValue[name].value) !== value) {
        const editedTaskList = {
          ...taskListValue,
          [name]: { ...taskListValue[name], value },
        };

        applyChangesToForm(editedTaskList);
      }
    }
  };

  const handleOperationValueChange = (e) => {
    const { value } = e.target;
    if (value.length <= 40) {
      setOperationValue(value);
    }
  };

  const handleVisitTypeChange = (taskListId) => {
    if (taskList?.id && taskListId && taskList?.id === taskListId) {
      return;
    }
    if (taskListId) {
      getTaskListRequest({
        orderId,
        taskListId,
      });
    }
  };

  const renderHeader = () => <ThemeHeader inlineTable={true}>
    {translate({ id: 'order.taskList.OPERATIONS' })}
  </ThemeHeader>;

  const renderFooter = () => {
    const value = form.getFieldValue('taskList');

    if (!value) {
      return null;
    }

    return (
      <PermissionCheck permissionsList={['ordercreate']}>
        <StyledOperationFooter type='flex' gutter={16}>
          <Col span={6}>
            <Input
              placeholder={translate({
                id: 'order.taskList.ENTER_DESCRIPTION',
              })}
              value={operationValue}
              disabled={disabled || isLoading}
              onChange={handleOperationValueChange}
              onPressEnter={handleAddOperation}
            />
          </Col>
          <Col span={8}>
            <Button
              onClick={handleAddOperation}
              disabled={!operationValue || disabled || isLoading}
              type='secondary'
            >
              <FormattedMessage id='order.taskList.ADD' />
            </Button>
          </Col>
        </StyledOperationFooter>
      </PermissionCheck>
    );
  };

  const operationStatusses = {
    predictive: 'PRED',
  };

  const getOperationsColumns = () => [
    {
      id: 'actions',
      displayName: '',
      fieldName: '',
      width: '30px',
      renderFn: r => <Icons>
        {isDraft && <PermissionCheck permissionsList={['ordercreate']}>
          <RowIconAction
            title={translate({ id: 'order.tooltip.deleteOperation' })}
            tooltipKey='delete'
            iconKey='trash-alt'
            action={() => handleDeleteOperation(r.id)}
            disabled={disabled || isLoading}
          />
        </PermissionCheck>}
      </Icons>,
    },
    {
      displayName: translate({ id: 'order.taskList.OPERATIONS' }),
      fieldName: 'description',
      id: 'operations',
      tooltip: row => row.description,
    },
    isToggleEnabled('ticketSmartActionUsePredOperationStateInOrder') && {
      displayName: translate({ id: 'ticket.smart_actions.ORIGIN' }),
      fieldName: 'operationStatus',
      id: 'operationStatus',
      renderFn: (r) => {
        if (r?.operationStatus === operationStatusses.predictive) {
          return translate({ id: 'ticket.smart_actions.PREDICTIVE' });
        }
        return translate({ id: 'ticket.smart_actions.NORMAL' });
      },
    },
  ]?.filter(v => v);

  form.getFieldDecorator('taskList', {
    initialValue: taskList,
  });

  if (!orderId) {
    return null;
  }

  return (
    <Div marginTop={45}>
      <SubTitle>{translate({ id: 'common.titles.TASKLIST' })}</SubTitle>
      <FixedOperations
        orderId={orderId}
        taskList={formTaskLists}
        fromScratch={fromScratch}
        isDraft={isDraft}
        disabled={disabled || isLoading}
        onOperationFieldChange={handleOperationFieldChange}
        onVisitTypeChange={handleVisitTypeChange}
        form={form}
        activeStatus={activeStatus}
      />
      <Wrapper marginBottom={10}>
        <Table
          disabled={disabled || isLoading}
          labelInColumn={false}
          movableRows={isDraft && !(disabled && isLoading)}
          columns={getOperationsColumns()}
          data={
            operations?.filter(o => o.changeIndicator !== CHANGE_INDICATOR_DELETE)
              ?.map((v, i) => ({
                ...v,
                index: i,
              }))
          }
          totalDataCount={operations.length}
          fullWidth={true}
          renderCustomFooter={isDraft ? renderFooter : null}
          renderCustomHeader={renderHeader}
          onDrop={handleDrop}
        />
      </Wrapper>
    </Div>
  );
};

TaskList.propTypes = {
  taskList: PropTypes.object,
  isDraft: PropTypes.bool,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  fromScratch: PropTypes.bool,
  intl: PropTypes.object.isRequired,
  form: PropTypes.object,
  setFormDirty: PropTypes.func,
  orderId: PropTypes.string,
  getTaskListRequest: PropTypes.func,
  activeStatus: PropTypes.object,
};

export default injectIntl(TaskList);
